/**
 * Cart store — module-level state with event emitter pattern.
 *
 * Intentionally simple for M0/M1. No external dependencies.
 * Each cart item: { id, product, size, addedAt }
 *
 * TODO(M2): Replace with React Context + Zustand for proper SSR-safe
 * state management and localStorage persistence across sessions.
 */

let _cart = [];
let _nextId = 1;
const _listeners = new Set();

/**
 * Notify all subscribers of a cart change.
 * @private
 */
function _notify() {
  _listeners.forEach((fn) => {
    try {
      fn([..._cart]);
    } catch (err) {
      // Silently drop listener errors — don't let a bad subscriber break cart ops
    }
  });
}

/**
 * Returns a shallow copy of the current cart items.
 * @returns {Array<{id: number, product: object, size: string|null, addedAt: string}>}
 */
export function getCart() {
  return [..._cart];
}

/**
 * Add a product to the cart.
 * If the same product+size combination already exists, increments quantity.
 * (Quantity tracking is a TODO — for M0 we just add a new line item.)
 *
 * @param {object} product - Product object from PRODUCTS array
 * @param {string|null} size - Selected size, or null for size-less items
 * @returns {object} The new cart item
 */
export function addItem(product, size = null) {
  if (!product || !product.id) {
    throw new Error('addItem: product must have an id');
  }

  // TODO(M2): Handle quantity — if same product+size exists, increment qty instead of adding a new line
  const item = {
    id: _nextId++,
    product,
    size,
    addedAt: new Date().toISOString(),
  };

  _cart = [..._cart, item];
  _notify();
  return item;
}

/**
 * Remove a cart item by its cart item ID (not product ID).
 * @param {number} itemId
 */
export function removeItem(itemId) {
  const prev = _cart.length;
  _cart = _cart.filter((item) => item.id !== itemId);
  if (_cart.length !== prev) {
    _notify();
  }
}

/**
 * Clear all items from the cart.
 * Called after successful order placement.
 */
export function clearCart() {
  _cart = [];
  _nextId = 1;
  _notify();
}

/**
 * Subscribe to cart changes. The callback receives the current cart array.
 * Returns an unsubscribe function.
 *
 * @param {function} fn - Callback invoked whenever cart state changes
 * @returns {function} Unsubscribe function
 *
 * @example
 * const unsubscribe = subscribe((items) => setCartItems(items));
 * // later:
 * unsubscribe();
 */
export function subscribe(fn) {
  if (typeof fn !== 'function') {
    throw new Error('subscribe: argument must be a function');
  }
  _listeners.add(fn);
  return function unsubscribe() {
    _listeners.delete(fn);
  };
}

/**
 * Returns the total number of items in the cart.
 * @returns {number}
 */
export function getItemCount() {
  return _cart.length;
}

/**
 * Returns the subtotal price of all items in the cart.
 * @returns {number}
 */
export function getSubtotal() {
  return _cart.reduce((sum, item) => sum + (item.product.price || 0), 0);
}
